import type { MessageKey } from '@autho/i18n';
import { Check, FileText, Sparkles, User } from 'lucide-react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { cn } from './cn.ts';

export type Confidence = 'stated' | 'confirmed' | 'extracted' | 'inferred';

const ICONS = { stated: User, confirmed: Check, extracted: FileText, inferred: Sparkles } as const;

/**
 * Design Principles #2: confidence looks different, and never by colour alone —
 * each level has its own icon; inferred is also italic with a dashed underline.
 */
export function ConfidenceBadge({ level, className }: { level: Confidence; className?: string }) {
  const intl = useIntl();
  const Icon = ICONS[level];
  const label = intl.formatMessage({ id: `confidence.${level}` satisfies MessageKey });
  const description = intl.formatMessage({
    id: `confidence.${level}.description` satisfies MessageKey,
  });
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded border px-1.5 py-px text-xs leading-5',
        level === 'inferred'
          ? 'border-dashed border-inferred/60 text-inferred italic'
          : 'border-border text-muted',
        className,
      )}
      title={description}
      data-confidence={level}
    >
      <Icon aria-hidden className="size-3" strokeWidth={2} />
      {label}
    </span>
  );
}

/** Wraps a displayed value so inferred values are visually distinct wherever they appear. */
export function ConfidenceValue({ level, children }: { level: Confidence; children: ReactNode }) {
  return (
    <span
      className={cn(
        level === 'inferred' &&
          'text-muted italic underline decoration-dashed decoration-inferred underline-offset-4',
      )}
    >
      {children}
    </span>
  );
}
